import React, { useEffect, useState } from 'react';
import { staffAPI, EQUIPMENT_CATEGORIES, GENDER_OPTIONS, PRESET_SHIFTS } from '../../api/staff';
import UserAvatar from '../UserAvatar';
import { X, Check, Package } from 'lucide-react';
import { toast } from 'sonner';

export default function MassEquipmentModal({ eventId, assignments, onDone, onClose }) {
  const [step, setStep] = useState(0);
  const [target, setTarget] = useState('all'); // all | selection | shift | gender
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectedShift, setSelectedShift] = useState('');
  const [selectedGender, setSelectedGender] = useState('');
  const [catalog, setCatalog] = useState([]);
  const [equipment, setEquipment] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    staffAPI.catalogList({ is_active: true }).then(r => setCatalog(r.data.items || []));
  }, []);

  const getTargetIds = () => {
    if (target === 'all') return assignments.map(a => a.staff_member_id);
    if (target === 'selection') return selectedIds;
    if (target === 'shift') return assignments.filter(a => a.shift?.name === selectedShift).map(a => a.staff_member_id);
    if (target === 'gender') return assignments.filter(a => (a.staff?.gender || '') === selectedGender).map(a => a.staff_member_id);
    return [];
  };

  const targetIds = getTargetIds();
  const equippedItems = Object.entries(equipment).filter(([, v]) => v.enabled);

  const handleConfirm = async () => {
    if (equippedItems.length === 0) { toast.error('Selecciona al menos un artículo'); return; }
    if (targetIds.length === 0) { toast.error('Selecciona al menos una persona'); return; }
    setSaving(true);
    try {
      // Backend EquipmentAssignInput expects quantity_assigned and
      // condition_on_delivery; previously sent `quantity`/`condition`
      // which pydantic silently dropped (#164).
      const equip = equippedItems.map(([id, v]) => ({
        equipment_id: id,
        quantity_assigned: parseInt(v.qty) || 1,
        condition_on_delivery: v.condition || 'bueno',
      }));
      const r = await staffAPI.bulkEquipment(eventId, targetIds, equip);
      toast.success(`Equipo asignado a ${r.data.updated || targetIds.length} persona(s)`);
      onDone();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al asignar equipo');
    } finally { setSaving(false); }
  };

  const shifts = [...new Set(assignments.map(a => a.shift?.name).filter(Boolean))];
  const toggleEq = (id) => setEquipment(prev => ({ ...prev, [id]: { ...prev[id], enabled: !prev[id]?.enabled } }));
  const setEqField = (id, field, val) => setEquipment(prev => ({ ...prev, [id]: { ...prev[id], [field]: val } }));

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
      <div className="bg-white rounded-sm shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col animate-fade-in">
        <div className="flex items-center justify-between px-5 py-4 border-b bg-soem-navy flex-shrink-0">
          <h3 className="font-bold text-white flex items-center gap-2"><Package size={18} /> Asignación Masiva de Equipo</h3>
          <button aria-label="Cerrar" onClick={onClose}><X size={18} className="text-slate-400 hover:text-white" /></button>
        </div>

        {/* Step bar */}
        <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2 flex-shrink-0 text-xs">
          {['¿A quién?', '¿Qué equipo?', 'Confirmar'].map((l, i) => (
            <React.Fragment key={i}>
              <span className={`flex items-center gap-1.5 font-semibold ${step === i ? 'text-soem-primary' : i < step ? 'text-green-600' : 'text-slate-400'}`}>
                <span className={`w-5 h-5 rounded-full flex items-center justify-center border ${step === i ? 'border-soem-primary text-soem-primary' : i < step ? 'bg-green-600 border-green-600 text-white' : 'border-slate-300'}`}>
                  {i < step ? '✓' : i + 1}
                </span>
                {l}
              </span>
              {i < 2 && <div className={`flex-1 h-0.5 ${i < step ? 'bg-green-600' : 'bg-slate-200'}`} />}
            </React.Fragment>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {/* Step 0: Target */}
          {step === 0 && (
            <div className="space-y-3">
              {[
                { id: 'all', label: `A todo el personal del evento (${assignments.length} personas)` },
                { id: 'selection', label: 'A un grupo específico' },
                { id: 'shift', label: 'Por turno' },
                { id: 'gender', label: 'Por género' },
              ].map(opt => (
                <label key={opt.id} className={`flex items-center gap-3 p-3 rounded-sm border cursor-pointer transition-colors ${target === opt.id ? 'border-soem-primary bg-blue-50' : 'border-slate-200 hover:border-slate-300'}`}>
                  <input type="radio" name="target" value={opt.id} checked={target === opt.id} onChange={() => setTarget(opt.id)} />
                  <span className="text-sm font-medium text-soem-navy">{opt.label}</span>
                </label>
              ))}

              {target === 'selection' && (
                <div className="border border-slate-200 rounded-sm divide-y divide-slate-50 max-h-52 overflow-y-auto">
                  {assignments.map(a => {
                    const avatarUser = { id: a.staff_member_id, name: a.staff?.full_name || '' };
                    const sel = selectedIds.includes(a.staff_member_id);
                    return (
                      <div key={a.id} onClick={() => setSelectedIds(prev => sel ? prev.filter(i => i !== a.staff_member_id) : [...prev, a.staff_member_id])}
                        className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer ${sel ? 'bg-blue-50' : 'hover:bg-slate-50'}`}>
                        <div className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 ${sel ? 'bg-soem-primary border-soem-primary' : 'border-slate-300'}`}>
                          {sel && <Check size={10} className="text-white" />}
                        </div>
                        <UserAvatar user={avatarUser} size="xs" />
                        <span className="text-sm">{a.staff?.full_name}</span>
                      </div>
                    );
                  })}
                </div>
              )}

              {target === 'shift' && (
                <select className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-soem-primary"
                  value={selectedShift} onChange={e => setSelectedShift(e.target.value)}>
                  <option value="">— Seleccionar turno —</option>
                  {shifts.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              )}

              {target === 'gender' && (
                <select className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-soem-primary"
                  value={selectedGender} onChange={e => setSelectedGender(e.target.value)}>
                  <option value="">— Seleccionar género —</option>
                  {Object.entries(GENDER_OPTIONS).map(([k, v]) => <option key={k} value={k}>{v.emoji} {v.label}</option>)}
                </select>
              )}

              <p className="text-xs text-slate-400">Personas seleccionadas: <strong>{targetIds.length}</strong></p>
            </div>
          )}

          {/* Step 1: Equipment */}
          {step === 1 && (
            <div className="space-y-2">
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm text-slate-500">Selecciona los artículos a asignar</p>
                <button type="button" onClick={() => {
                  const def = catalog.slice(0, 5);
                  const upd = {};
                  def.forEach(eq => { upd[eq.id] = { enabled: true, qty: 1, condition: 'bueno' }; });
                  setEquipment(upd);
                }} className="text-xs text-soem-primary hover:underline">
                  ⭐ Seleccionar defaults
                </button>
              </div>
              {catalog.map(eq => {
                const cat = EQUIPMENT_CATEGORIES[eq.category] || EQUIPMENT_CATEGORIES.otro;
                const state = equipment[eq.id] || {};
                return (
                  <div key={eq.id} className={`border rounded-sm p-3 ${state.enabled ? 'border-soem-primary bg-blue-50' : 'border-slate-200'}`}>
                    <div className="flex items-center gap-3">
                      <button type="button" onClick={() => toggleEq(eq.id)}
                        className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${state.enabled ? 'bg-soem-primary border-soem-primary' : 'border-slate-300'}`}>
                        {state.enabled && <Check size={11} className="text-white" />}
                      </button>
                      <span className="text-sm font-medium text-soem-navy">{cat.emoji} {eq.name}</span>
                    </div>
                    {state.enabled && (
                      <div className="grid grid-cols-2 gap-2 mt-2 ml-8">
                        <div>
                          <label htmlFor={`f-qty-${eq.id}`} className="soem-label block mb-0.5">Cantidad</label>
                          <input id={`f-qty-${eq.id}`} type="number" min="1" className="w-full px-2 py-1.5 border border-slate-300 rounded-sm text-sm focus:outline-none"
                            value={state.qty || 1} onChange={e => setEqField(eq.id, 'qty', e.target.value)} />
                        </div>
                        <div>
                          <label htmlFor={`f-cond-${eq.id}`} className="soem-label block mb-0.5">Condición</label>
                          <select id={`f-cond-${eq.id}`} className="w-full px-2 py-1.5 border border-slate-300 rounded-sm text-sm focus:outline-none"
                            value={state.condition || 'bueno'} onChange={e => setEqField(eq.id, 'condition', e.target.value)}>
                            {['bueno','regular','malo'].map(c => <option key={c} value={c}>{c}</option>)}
                          </select>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Step 2: Confirm */}
          {step === 2 && (
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-sm p-4 text-center">
                <p className="font-bold text-soem-navy text-lg">{equippedItems.length} artículo(s)</p>
                <p className="text-slate-500 text-sm">para <strong>{targetIds.length} persona(s)</strong></p>
              </div>
              <div className="space-y-2">
                {equippedItems.map(([id, v]) => {
                  const eq = catalog.find(c => c.id === id);
                  const cat = EQUIPMENT_CATEGORIES[eq?.category] || EQUIPMENT_CATEGORIES.otro;
                  return (
                    <div key={id} className="flex items-center justify-between text-sm py-1.5 border-b border-slate-100">
                      <span className="text-soem-navy font-medium">{cat.emoji} {eq?.name}</span>
                      <span className="text-slate-500">{v.qty || 1} u. — {v.condition || 'bueno'}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <div className="px-5 py-4 border-t border-slate-100 flex gap-3 flex-shrink-0">
          {step > 0 && <button onClick={() => setStep(s => s - 1)} className="soem-btn-secondary px-4 py-2.5 text-sm">← Anterior</button>}
          {step < 2 ? (
            <button onClick={() => setStep(s => s + 1)} disabled={targetIds.length === 0}
              className="soem-btn-primary flex-1 py-2.5 text-sm disabled:opacity-60">
              Siguiente →
            </button>
          ) : (
            <button onClick={handleConfirm} disabled={saving || equippedItems.length === 0}
              className="soem-btn-primary flex-1 py-2.5 text-sm flex items-center justify-center gap-2 disabled:opacity-60"
              data-testid="confirm-bulk-btn">
              {saving ? 'Asignando...' : '✓ Confirmar asignación masiva'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
